import {
  createStyleMixin,
  draggableMixin,
  createProxy,
  proxyInputMixin,
  componentNameValidationMixin,
} from '@descope-ui/common/components-mixins';
import {
  getComponentName,
  forwardAttrs,
  syncAttrs,
} from '@descope-ui/common/components-helpers';
import { compose } from '@descope-ui/common/utils';
import { componentName as descopeInternalComponentName } from './descope-address-field-internal/AddressFieldInternal';

export const componentName = getComponentName('address-field');

const customMixin = (superclass) =>
  class AddressFieldMixinClass extends superclass {
    get defaultValue() {
      return this.getAttribute('default-value');
    }

    setDefaultValue() {
      if (this.defaultValue) {
        setTimeout(() => {
          this.inputElement.value = this.defaultValue;
        });
      }
    }

    set connectorClasses(value) {
      this.inputElement.connectorClasses = value;
    }

    get connectorClasses() {
      return this.inputElement.connectorClasses;
    }

    init() {
      super.init?.();
      const template = document.createElement('template');

      template.innerHTML = `
      <${descopeInternalComponentName}
        tabindex="-1"
      ></${descopeInternalComponentName}>
      `;

      this.baseElement.appendChild(template.content.cloneNode(true));

      this.inputElement = this.shadowRoot.querySelector(
        descopeInternalComponentName,
      );

      forwardAttrs(this, this.inputElement, {
        includeAttrs: [
          'size',
          'bordered',
          'label',
          'required',
          'label-type',
          'placeholder',
          'full-width',
          'allow-custom-value',
          'min-search-length',
          'no-results-message',
          'error-fetching-results-message',
          'data-errormessage-value-missing',
          'invalid',
          'error-message',
          'readonly',
          'disabled',
          'st-host-direction',
          'st-error-message-icon',
          'st-error-message-icon-size',
          'st-error-message-icon-padding',
          'connector-template',
          'public-api-key',
          'address-types',
          'address-language',
          'address-region',
          'address-limit',
        ],
      });

      // This is required since when we remove the invalid attribute from the internal mappings field,
      // we want to reflect the change in the parent component
      syncAttrs(this, this.inputElement, { includeAttrs: ['invalid'] });
      this.setDefaultValue();
    }
  };

const { host } = {
  host: { selector: () => ':host' },
};

export const AddressFieldClass = compose(
  createStyleMixin({
    componentNameOverride: getComponentName('input-wrapper'),
  }),
  createStyleMixin({
    mappings: {
      hostWidth: { ...host, property: 'width' },
      hostDirection: { ...host, property: 'direction' },
    },
  }),
  draggableMixin,
  proxyInputMixin({
    proxyProps: ['value', 'selectionStart'],
    inputEvent: 'input',
    proxyParentValidation: true,
  }),
  componentNameValidationMixin,
  customMixin,
)(
  createProxy({
    slots: [],
    wrappedEleName: 'vaadin-custom-field',
    style: () => `
  :host {
    display: inline-flex;
    max-width: 100%;
    direction: ltr;
  }
  vaadin-custom-field {
    line-height: unset;
    width: 100%;
  }

  ${descopeInternalComponentName} {
    display: inline-block;
    box-sizing: border-box;
    user-select: none;
    width: 100%;
    max-width: 100%;
  }

  ${descopeInternalComponentName} ::slotted {
    padding: 0;
  }
`,
    excludeAttrsSync: ['tabindex', 'error-message', 'label', 'style'],
    componentName,
  }),
);
